import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Users as UsersIcon, Coins, BarChart3, ListTree, Settings as SettingsIcon, Layers,
  Snowflake, Plus, Trash2, Pencil, KeyRound, Building2, Globe2, Copy, ArrowLeft, Eye,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

const ROLES = ['admin', 'coordinator', 'team_member', 'photographer'];
const STATUSES = ['active', 'frozen', 'cancelled'];

function StatCard({ label, value, hint }) {
  return (
    <Card className="p-5">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </Card>
  );
}

function DashboardTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/admin/dashboard').then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <p className="text-slate-500">{t('common.loading')}</p>;
  const u = data.users || {};
  const c = data.credits || {};
  const b = data.billing || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <StatCard label={t('admin.dashboard.totalUsers')} value={u.total ?? 0} />
      <StatCard label={t('admin.dashboard.activeUsers')} value={u.active ?? 0} />
      <StatCard label={t('admin.dashboard.frozenUsers')} value={u.frozen ?? 0} />
      <StatCard label={t('admin.dashboard.trialUsers')} value={u.trial ?? 0} />
      <StatCard label={t('admin.dashboard.paidUsers')} value={u.paid ?? 0} />
      <StatCard label={t('admin.dashboard.mrr')} value={`$${(b.mrr_estimate ?? 0).toFixed(2)}`} hint={b.currency} />
      <StatCard label={t('admin.dashboard.creditsIssued')} value={Math.round(c.total_issued ?? 0)} />
      <StatCard label={t('admin.dashboard.creditsUsed')} value={Math.round(c.total_used ?? 0)} />
      <StatCard label={t('admin.dashboard.creditsOutstanding')} value={Math.round(c.outstanding ?? 0)} />
    </div>
  );
}

function UsersTab() {
  const { t } = useTranslation();
  const { user: me, impersonate } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creditModal, setCreditModal] = useState(null);
  const [extendModal, setExtendModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (roleFilter && roleFilter !== 'all') params.role = roleFilter;
      if (planFilter && planFilter !== 'all') params.plan_id = planFilter;
      if (statusFilter && statusFilter !== 'all') params.status_filter = statusFilter;
      const r = await api.get('/admin/users-detailed', { params });
      setUsers(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get('/admin/subscription-plans').then((r) => setPlans(r.data || []));
    load();
  }, []); // eslint-disable-line

  const onFreeze = async (u) => {
    await api.post(`/admin/users/${u.id}/freeze`, null, { params: { reason: 'manual' } });
    toast.success(t('admin.users.saved'));
    load();
  };
  const onUnfreeze = async (u) => {
    await api.post(`/admin/users/${u.id}/unfreeze`);
    toast.success(t('admin.users.saved'));
    load();
  };

  const onSaveEdit = async () => {
    const payload = {
      role: editing.role, plan_id: editing.plan_id,
      status: editing.status, name: editing.name,
    };
    await api.patch(`/admin/users/${editing.id}/saas`, payload);
    toast.success(t('admin.users.saved'));
    setEditing(null);
    load();
  };

  const onDelete = async () => {
    try {
      await api.delete(`/admin/users/${deleteModal.id}`);
      toast.success(t('admin.users.deleted'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.users.deleteFailed'));
    } finally {
      setDeleteModal(null);
      load();
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <Input
          placeholder={t('admin.users.search')} value={q}
          onChange={(e) => setQ(e.target.value)} className="max-w-xs"
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-44"><SelectValue placeholder={t('admin.users.filterRole')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">—</SelectItem>
            {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-44"><SelectValue placeholder={t('admin.users.filterPlan')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">—</SelectItem>
            {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44"><SelectValue placeholder={t('admin.users.filterStatus')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">—</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={load}>{t('common.search')}</Button>
      </div>

      {loading ? <p className="text-slate-500">{t('common.loading')}</p> : users.length === 0 ? (
        <p className="text-slate-500">{t('admin.users.noUsers')}</p>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="p-2">{t('admin.users.name')}</th>
                <th className="p-2">{t('admin.users.email')}</th>
                <th className="p-2">{t('admin.users.role')}</th>
                <th className="p-2">{t('admin.users.plan')}</th>
                <th className="p-2">{t('admin.users.status')}</th>
                <th className="p-2 text-right">{t('admin.users.credits')}</th>
                <th className="p-2 text-right">{t('admin.users.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t">
                  <td className="p-2">{u.name || '—'}</td>
                  <td className="p-2 text-slate-700">{u.email}</td>
                  <td className="p-2"><span className="text-xs px-2 py-0.5 rounded bg-slate-100">{u.role}</span></td>
                  <td className="p-2">{u.plan_id}</td>
                  <td className="p-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      u.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                      u.status === 'frozen' ? 'bg-amber-100 text-amber-700' :
                      'bg-rose-100 text-rose-700'
                    }`}>{u.status}</span>
                  </td>
                  <td className="p-2 text-right">{Math.round(u.credit_balance || 0)}</td>
                  <td className="p-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ ...u })}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setCreditModal({ user: u, amount: 0, reason: '' })}>
                        <Coins className="w-3.5 h-3.5" />
                      </Button>
                      {u.id !== me?.id && (
                        <Button size="sm" variant="ghost" title={t('admin.users.viewAs')}
                          onClick={async () => {
                            try {
                              await impersonate(u.id);
                              navigate('/dashboard');
                            } catch {
                              toast.error(t('admin.users.viewAsFailed'));
                            }
                          }}>
                          <Eye className="w-3.5 h-3.5 text-sky-600" />
                        </Button>
                      )}
                      {u.plan_id === 'free_trial' && (
                        <Button size="sm" variant="ghost" onClick={() => setExtendModal({ user: u, days: 7 })} title={t('admin.users.extendTrial')}>
                          +d
                        </Button>
                      )}
                      {u.status === 'frozen' ? (
                        <Button size="sm" variant="ghost" onClick={() => onUnfreeze(u)} title={t('admin.users.unfreeze')}>
                          <Snowflake className="w-3.5 h-3.5 text-amber-600" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => onFreeze(u)} title={t('admin.users.freeze')}>
                          <Snowflake className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {u.id !== me?.id && (
                        <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700"
                          onClick={() => setDeleteModal(u)} title={t('admin.users.delete')}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.email}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>{t('admin.users.name')}</Label>
                <Input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <Label>{t('admin.users.role')}</Label>
                <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('admin.users.plan')}</Label>
                <Select value={editing.plan_id} onValueChange={(v) => setEditing({ ...editing, plan_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('admin.users.status')}</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
            <Button onClick={onSaveEdit}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!creditModal} onOpenChange={(v) => !v && setCreditModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('admin.users.adjustCredits')} — {creditModal?.user?.email}</DialogTitle></DialogHeader>
          {creditModal && (
            <div className="space-y-3">
              <div>
                <Label>{t('admin.users.creditsAmount')}</Label>
                <Input type="number" value={creditModal.amount}
                  onChange={(e) => setCreditModal({ ...creditModal, amount: parseFloat(e.target.value) || 0 })} />
                <p className="text-xs text-slate-500 mt-1">{t('admin.users.creditsHint')}</p>
              </div>
              <div>
                <Label>{t('admin.users.creditsReason')}</Label>
                <Input value={creditModal.reason} onChange={(e) => setCreditModal({ ...creditModal, reason: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreditModal(null)}>{t('common.cancel')}</Button>
            <Button onClick={async () => {
              await api.post(`/admin/users/${creditModal.user.id}/credits`, {
                amount: creditModal.amount, reason: creditModal.reason,
              });
              toast.success(t('admin.users.saved'));
              setCreditModal(null);
              load();
            }}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!extendModal} onOpenChange={(v) => !v && setExtendModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('admin.users.extendTrial')} — {extendModal?.user?.email}</DialogTitle></DialogHeader>
          {extendModal && (
            <div>
              <Label>{t('admin.users.extendTrialDays')}</Label>
              <Input type="number" value={extendModal.days}
                onChange={(e) => setExtendModal({ ...extendModal, days: parseInt(e.target.value) || 0 })} />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExtendModal(null)}>{t('common.cancel')}</Button>
            <Button onClick={async () => {
              await api.patch(`/admin/users/${extendModal.user.id}/saas`, { extend_trial_days: extendModal.days });
              toast.success(t('admin.users.saved'));
              setExtendModal(null);
              load();
            }}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteModal} onOpenChange={(v) => !v && setDeleteModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('admin.users.delete')}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            {t('admin.users.deleteConfirm', { email: deleteModal?.email })}
          </p>
          <p className="text-xs text-rose-600">{t('admin.users.deleteWarning')}</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteModal(null)}>{t('common.cancel')}</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={onDelete}>
              {t('admin.users.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubscriptionsTab() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get('/admin/subscriptions').then((r) => setRows(r.data || []));
  }, []);
  return (
    <Card className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-left">
          <tr>
            <th className="p-2">{t('admin.users.plan')}</th>
            <th className="p-2">{t('admin.users.status')}</th>
            <th className="p-2 text-right">Count</th>
            <th className="p-2 text-right">Total {t('admin.users.credits')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t">
              <td className="p-2">{r.plan_id}</td>
              <td className="p-2">{r.status}</td>
              <td className="p-2 text-right">{r.count}</td>
              <td className="p-2 text-right">{Math.round(r.total_credits || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function PlansTab() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);

  const load = () => api.get('/admin/subscription-plans').then((r) => setPlans(r.data || []));
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    const target = editing || creating;
    const body = {
      ...target,
      features: typeof target.features === 'string'
        ? target.features.split(',').map((s) => s.trim()).filter(Boolean)
        : (target.features || []),
    };
    if (creating) {
      await api.post('/admin/subscription-plans', body);
    } else {
      const { id, created_at, ...patch } = body;
      await api.patch(`/admin/subscription-plans/${id}`, patch);
    }
    toast.success(t('admin.plans.saved'));
    setEditing(null);
    setCreating(null);
    load();
  };

  const onDelete = async (p) => {
    if (!window.confirm(t('admin.plans.deleteConfirm'))) return;
    try {
      await api.delete(`/admin/subscription-plans/${p.id}`);
      toast.success(t('admin.plans.saved'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('auth.errors.generic'));
    }
  };

  const target = editing || creating;
  const setTarget = (v) => (editing ? setEditing(v) : setCreating(v));

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setCreating({
          id: '', name: '', description: '',
          monthly_price: 0, yearly_price: 0,
          monthly_credits: 0, trial_credits: 0, trial_days: 0,
          features: [], is_active: true, is_public: true,
        })}>
          <Plus className="w-4 h-4 mr-1" /> {t('admin.plans.create')}
        </Button>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="p-2">{t('admin.plans.id')}</th>
              <th className="p-2">{t('admin.plans.name')}</th>
              <th className="p-2 text-right">{t('admin.plans.monthlyPrice')}</th>
              <th className="p-2 text-right">{t('admin.plans.monthlyCredits')}</th>
              <th className="p-2">{t('admin.plans.active')}</th>
              <th className="p-2">{t('admin.plans.public')}</th>
              <th className="p-2 text-right">{t('admin.users.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id} className="border-t">
                <td className="p-2 font-mono text-xs">{p.id}</td>
                <td className="p-2">{p.name}</td>
                <td className="p-2 text-right">${Number(p.monthly_price).toFixed(2)}</td>
                <td className="p-2 text-right">{p.monthly_credits}</td>
                <td className="p-2">{p.is_active ? '✓' : '—'}</td>
                <td className="p-2">{p.is_public ? '✓' : '—'}</td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing({
                    ...p, features: (p.features || []).join(', '),
                  })}><Pencil className="w-3.5 h-3.5" /></Button>
                  {p.id !== 'free_trial' && (
                    <Button size="sm" variant="ghost" onClick={() => onDelete(p)}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!target} onOpenChange={(v) => !v && (setEditing(null), setCreating(null))}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{creating ? t('admin.plans.create') : target?.id}</DialogTitle>
          </DialogHeader>
          {target && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('admin.plans.id')}</Label>
                <Input value={target.id} disabled={!creating}
                  onChange={(e) => setTarget({ ...target, id: e.target.value })} />
              </div>
              <div>
                <Label>{t('admin.plans.name')}</Label>
                <Input value={target.name}
                  onChange={(e) => setTarget({ ...target, name: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Input value={target.description || ''}
                  onChange={(e) => setTarget({ ...target, description: e.target.value })} />
              </div>
              <div>
                <Label>{t('admin.plans.monthlyPrice')}</Label>
                <Input type="number" value={target.monthly_price}
                  onChange={(e) => setTarget({ ...target, monthly_price: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>{t('admin.plans.yearlyPrice')}</Label>
                <Input type="number" value={target.yearly_price}
                  onChange={(e) => setTarget({ ...target, yearly_price: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>{t('admin.plans.monthlyCredits')}</Label>
                <Input type="number" value={target.monthly_credits}
                  onChange={(e) => setTarget({ ...target, monthly_credits: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>{t('admin.plans.trialCredits')}</Label>
                <Input type="number" value={target.trial_credits}
                  onChange={(e) => setTarget({ ...target, trial_credits: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>{t('admin.plans.trialDays')}</Label>
                <Input type="number" value={target.trial_days}
                  onChange={(e) => setTarget({ ...target, trial_days: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="flex items-end gap-3">
                <div className="flex items-center gap-2"><Switch checked={!!target.is_active}
                  onCheckedChange={(v) => setTarget({ ...target, is_active: v })} /> {t('admin.plans.active')}</div>
                <div className="flex items-center gap-2"><Switch checked={!!target.is_public}
                  onCheckedChange={(v) => setTarget({ ...target, is_public: v })} /> {t('admin.plans.public')}</div>
              </div>
              <div>
                <Label>{t('admin.plans.stripeMonthly')}</Label>
                <Input value={target.stripe_price_id_monthly || ''}
                  onChange={(e) => setTarget({ ...target, stripe_price_id_monthly: e.target.value })} />
              </div>
              <div>
                <Label>{t('admin.plans.stripeYearly')}</Label>
                <Input value={target.stripe_price_id_yearly || ''}
                  onChange={(e) => setTarget({ ...target, stripe_price_id_yearly: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>{t('admin.plans.features')}</Label>
                <Input value={target.features || ''}
                  onChange={(e) => setTarget({ ...target, features: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditing(null); setCreating(null); }}>{t('common.cancel')}</Button>
            <Button onClick={onSave}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreditsTab() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ user_id: '', type_filter: '', feature: '' });

  const load = async () => {
    const params = {};
    if (filters.user_id) params.user_id = filters.user_id;
    if (filters.type_filter) params.type_filter = filters.type_filter;
    if (filters.feature) params.feature = filters.feature;
    const r = await api.get('/admin/credit-transactions', { params });
    setRows(r.data || []);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <Input placeholder={t('admin.credits.filterUser')} value={filters.user_id}
          onChange={(e) => setFilters({ ...filters, user_id: e.target.value })} className="max-w-xs" />
        <Input placeholder={t('admin.credits.filterType')} value={filters.type_filter}
          onChange={(e) => setFilters({ ...filters, type_filter: e.target.value })} className="max-w-xs" />
        <Input placeholder={t('admin.credits.filterFeature')} value={filters.feature}
          onChange={(e) => setFilters({ ...filters, feature: e.target.value })} className="max-w-xs" />
        <Button onClick={load}>{t('common.search')}</Button>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="p-2">{t('admin.credits.date')}</th>
              <th className="p-2">{t('admin.credits.user')}</th>
              <th className="p-2">{t('admin.credits.type')}</th>
              <th className="p-2">{t('admin.credits.feature')}</th>
              <th className="p-2 text-right">{t('admin.credits.amount')}</th>
              <th className="p-2 text-right">{t('admin.credits.balance')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="p-2 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td className="p-2 font-mono text-xs">{r.user_id?.slice(0, 8)}…</td>
                <td className="p-2">{t(`credits.type.${r.type}`, r.type)}</td>
                <td className="p-2 text-xs">{r.feature ? t(`credits.feature.${r.feature}`, r.feature) : '—'}</td>
                <td className={`p-2 text-right ${r.amount < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {r.amount > 0 ? '+' : ''}{r.amount}
                </td>
                <td className="p-2 text-right">{Math.round(r.balance_after || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function SettingsTab() {
  const { t } = useTranslation();
  const [costs, setCosts] = useState({});
  const [trial, setTrial] = useState({ default_trial_days: 14, default_trial_credits: 50 });

  useEffect(() => {
    api.get('/admin/system-config/ai_credit_costs').then((r) => setCosts(r.data?.costs || {})).catch(() => {});
    api.get('/admin/system-config/trial_configuration').then((r) => setTrial({
      default_trial_days: r.data?.default_trial_days ?? 14,
      default_trial_credits: r.data?.default_trial_credits ?? 50,
    })).catch(() => {});
  }, []);

  const saveCosts = async () => {
    await api.put('/admin/system-config/ai_credit_costs', { costs });
    toast.success(t('admin.settings.saved'));
  };
  const saveTrial = async () => {
    await api.put('/admin/system-config/trial_configuration', trial);
    toast.success(t('admin.settings.saved'));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="p-5">
        <h3 className="font-semibold mb-3">{t('admin.settings.aiCosts')}</h3>
        <div className="space-y-2">
          {Object.entries(costs).map(([k, v]) => (
            <div key={k} className="grid grid-cols-2 gap-2 items-center">
              <span className="text-sm text-slate-700">{t(`credits.feature.${k}`, k)}</span>
              <Input type="number" value={v}
                onChange={(e) => setCosts({ ...costs, [k]: parseFloat(e.target.value) || 0 })} />
            </div>
          ))}
        </div>
        <Button className="mt-4" onClick={saveCosts}>{t('common.save')}</Button>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold mb-3">{t('admin.settings.trialDefaults')}</h3>
        <div className="space-y-3">
          <div>
            <Label>{t('admin.settings.trialDaysDefault')}</Label>
            <Input type="number" value={trial.default_trial_days}
              onChange={(e) => setTrial({ ...trial, default_trial_days: parseInt(e.target.value) || 0 })} />
          </div>
          <div>
            <Label>{t('admin.settings.trialCreditsDefault')}</Label>
            <Input type="number" value={trial.default_trial_credits}
              onChange={(e) => setTrial({ ...trial, default_trial_credits: parseFloat(e.target.value) || 0 })} />
          </div>
        </div>
        <Button className="mt-4" onClick={saveTrial}>{t('common.save')}</Button>
      </Card>
    </div>
  );
}

function codeStatus(c) {
  if (!c.is_active) return 'statusRevoked';
  if (c.expires_at && new Date(c.expires_at) < new Date()) return 'statusExpired';
  return 'statusActive';
}

function AccessCodesTab() {
  const { t } = useTranslation();
  const [codes, setCodes] = useState([]);
  const [plans, setPlans] = useState([]);
  const [gateEnabled, setGateEnabled] = useState(false);
  const [sharedLabel, setSharedLabel] = useState('');
  const [inv, setInv] = useState({
    count: 5, bound_email: '', grant_plan_id: 'free_trial',
    expires_at: '', grant_trial_days: '', grant_trial_credits: '', label: '',
  });

  const load = async () => {
    const [c, p] = await Promise.all([
      api.get('/admin/registration-codes'),
      api.get('/admin/subscription-plans'),
    ]);
    setCodes(c.data || []);
    setPlans(p.data || []);
    try {
      const g = await api.get('/admin/system-config/registration_gate');
      setGateEnabled(!!g.data?.enabled);
    } catch { setGateEnabled(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const sharedCode = codes.find((c) => c.kind === 'shared' && c.is_active);
  const hasActiveCode = codes.some((c) => codeStatus(c) === 'statusActive');

  const toggleGate = async (enabled) => {
    await api.put('/admin/system-config/registration_gate', { enabled });
    setGateEnabled(enabled);
    toast.success(t('admin.settings.saved'));
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success(t('admin.accessCodes.copied'));
  };

  const genShared = async () => {
    await api.post('/admin/registration-codes', { kind: 'shared', label: sharedLabel || 'Trial' });
    setSharedLabel('');
    await load();
  };

  const genInvites = async () => {
    const body = {
      kind: 'invite',
      count: parseInt(inv.count) || 1,
      label: inv.label || '',
      grant_plan_id: inv.grant_plan_id || 'free_trial',
      max_uses: 1,
    };
    if (inv.bound_email) body.bound_email = inv.bound_email;
    if (inv.expires_at) body.expires_at = inv.expires_at;
    if (inv.grant_trial_days !== '') body.grant_trial_days = parseInt(inv.grant_trial_days);
    if (inv.grant_trial_credits !== '') body.grant_trial_credits = parseFloat(inv.grant_trial_credits);
    const r = await api.post('/admin/registration-codes', body);
    const created = (r.data?.created || []).map((c) => c.code).join('  ');
    toast.success(`${t('admin.accessCodes.generated')}: ${created}`);
    await load();
  };

  const revoke = async (id) => {
    await api.delete(`/admin/registration-codes/${id}`);
    await load();
  };

  return (
    <div className="space-y-6">
      {/* Gate toggle */}
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">{t('admin.accessCodes.gateTitle')}</h3>
            <p className="text-sm text-slate-500 mt-1">{t('admin.accessCodes.gateHint')}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">{t('admin.accessCodes.gateLabel')}</span>
            <Switch checked={gateEnabled} onCheckedChange={toggleGate} />
          </div>
        </div>
        {!hasActiveCode && (
          <p className="text-sm text-amber-600 bg-amber-50 rounded-md px-3 py-2 mt-3">
            ⚠️ {t('admin.accessCodes.lockoutWarning')}
          </p>
        )}
      </Card>

      {/* Shared trial code */}
      <Card className="p-5">
        <h3 className="font-semibold">{t('admin.accessCodes.sharedTitle')}</h3>
        <p className="text-sm text-slate-500 mt-1">{t('admin.accessCodes.sharedHint')}</p>
        {sharedCode ? (
          <div className="flex items-center gap-3 mt-3">
            <span className="font-mono text-lg bg-slate-100 rounded-md px-4 py-2 tracking-wider">
              {sharedCode.code}
            </span>
            <Button variant="outline" size="sm" onClick={() => copy(sharedCode.code)}>
              <Copy className="w-4 h-4 mr-1" /> {t('admin.accessCodes.copy')}
            </Button>
            <span className="text-xs text-slate-500">
              {sharedCode.uses_count} {t('admin.accessCodes.colUses').toLowerCase()}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 mt-3">
            <Input placeholder={t('admin.accessCodes.label')} value={sharedLabel}
              onChange={(e) => setSharedLabel(e.target.value)} className="max-w-xs" />
            <Button onClick={genShared}>
              <Plus className="w-4 h-4 mr-1" /> {t('admin.accessCodes.generateShared')}
            </Button>
          </div>
        )}
      </Card>

      {/* Invite generator */}
      <Card className="p-5">
        <h3 className="font-semibold mb-3">{t('admin.accessCodes.generateTitle')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>{t('admin.accessCodes.count')}</Label>
            <Input type="number" min={1} max={200} value={inv.count}
              onChange={(e) => setInv({ ...inv, count: e.target.value })} />
          </div>
          <div>
            <Label>{t('admin.accessCodes.plan')}</Label>
            <Select value={inv.grant_plan_id} onValueChange={(v) => setInv({ ...inv, grant_plan_id: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('admin.accessCodes.boundEmail')}</Label>
            <Input type="email" value={inv.bound_email}
              onChange={(e) => setInv({ ...inv, bound_email: e.target.value })} />
          </div>
          <div>
            <Label>{t('admin.accessCodes.expiresAt')}</Label>
            <Input type="date" value={inv.expires_at}
              onChange={(e) => setInv({ ...inv, expires_at: e.target.value })} />
          </div>
          <div>
            <Label>{t('admin.accessCodes.trialDays')}</Label>
            <Input type="number" value={inv.grant_trial_days}
              onChange={(e) => setInv({ ...inv, grant_trial_days: e.target.value })} />
          </div>
          <div>
            <Label>{t('admin.accessCodes.trialCredits')}</Label>
            <Input type="number" value={inv.grant_trial_credits}
              onChange={(e) => setInv({ ...inv, grant_trial_credits: e.target.value })} />
          </div>
        </div>
        <Button className="mt-4" onClick={genInvites}>
          <Plus className="w-4 h-4 mr-1" /> {t('admin.accessCodes.generate')}
        </Button>
      </Card>

      {/* Codes table */}
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="p-2">{t('admin.accessCodes.colCode')}</th>
              <th className="p-2">{t('admin.accessCodes.colKind')}</th>
              <th className="p-2">{t('admin.accessCodes.colLabel')}</th>
              <th className="p-2">{t('admin.accessCodes.colUses')}</th>
              <th className="p-2">{t('admin.accessCodes.colEmail')}</th>
              <th className="p-2">{t('admin.accessCodes.colStatus')}</th>
              <th className="p-2">{t('admin.accessCodes.colExpires')}</th>
              <th className="p-2 text-right">{t('admin.accessCodes.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => {
              const st = codeStatus(c);
              return (
                <tr key={c.id} className="border-t">
                  <td className="p-2 font-mono">{c.code}</td>
                  <td className="p-2">{t(`admin.accessCodes.kind${c.kind === 'shared' ? 'Shared' : 'Invite'}`)}</td>
                  <td className="p-2">{c.label || '—'}</td>
                  <td className="p-2">{c.uses_count}/{c.max_uses ?? t('admin.accessCodes.unlimited')}</td>
                  <td className="p-2 text-xs">{c.bound_email || '—'}</td>
                  <td className="p-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      st === 'statusActive' ? 'bg-emerald-100 text-emerald-700'
                        : st === 'statusExpired' ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-200 text-slate-600'}`}>
                      {t(`admin.accessCodes.${st}`)}
                    </span>
                  </td>
                  <td className="p-2 text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}</td>
                  <td className="p-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => copy(c.code)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    {c.is_active && (
                      <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => revoke(c.id)}>
                        {t('admin.accessCodes.revoke')}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function MiniBreakdown({ title, data }) {
  const entries = Object.entries(data || {});
  return (
    <Card className="p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">{title}</p>
      <div className="space-y-1">
        {entries.length === 0 && <p className="text-sm text-slate-400">—</p>}
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm">
            <span className="text-slate-600">{k}</span>
            <span className="font-medium">{v}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function OverviewTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/admin/overview').then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <p className="text-slate-500">{t('common.loading')}</p>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <StatCard label={t('admin.overview.orgs')} value={data.orgs?.total ?? 0} />
        <StatCard label={t('admin.overview.users')} value={data.users?.total ?? 0} />
        <StatCard label={t('admin.overview.events')} value={data.events?.total ?? 0} />
        <StatCard label={t('admin.overview.activeEvents')} value={data.events?.active ?? 0} />
        <StatCard label={t('admin.overview.photographers')} value={data.photographers ?? 0} />
        <StatCard label={t('admin.overview.spots')} value={data.spots ?? 0} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MiniBreakdown title={t('admin.overview.byRole')} data={data.users?.by_role} />
        <MiniBreakdown title={t('admin.overview.byStatus')} data={data.users?.by_status} />
        <MiniBreakdown title={t('admin.overview.byPlan')} data={data.users?.by_plan} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">{t('admin.overview.recentSignups')}</p>
          <div className="space-y-1">
            {(data.recent_signups || []).map((u) => (
              <div key={u.id} className="flex justify-between text-sm">
                <span className="text-slate-700">{u.name || u.email}</span>
                <span className="text-xs text-slate-400">{u.plan_id}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">{t('admin.overview.recentEvents')}</p>
          <div className="space-y-1">
            {(data.recent_events || []).map((e) => (
              <div key={e.id} className="flex justify-between text-sm">
                <span className="text-slate-700">{e.name}</span>
                <span className="text-xs text-slate-400">{e.owner_email || '—'}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function OrgsTab() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const r = await api.get('/admin/orgs', { params: q ? { q } : {} });
    setRows(r.data?.orgs || []);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openOrg = async (orgId) => {
    const r = await api.get(`/admin/orgs/${orgId}`);
    setDetail(r.data);
  };

  if (detail) {
    const c = detail.counts || {};
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => setDetail(null)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> {t('admin.orgs.back')}
        </Button>
        <Card className="p-5">
          <h3 className="font-semibold text-lg">{detail.owner?.name || t('admin.orgs.unknownOwner')}</h3>
          <p className="text-sm text-slate-500">{detail.owner?.email} · {detail.owner?.plan_id} · {detail.owner?.status}</p>
          <p className="text-xs text-slate-400 font-mono mt-1">{detail.org_id}</p>
        </Card>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label={t('admin.orgs.events')} value={c.events ?? 0} />
          <StatCard label={t('admin.orgs.photographers')} value={c.photographers ?? 0} />
          <StatCard label={t('admin.orgs.plans')} value={c.plans ?? 0} />
          <StatCard label={t('admin.orgs.spots')} value={c.spots ?? 0} />
          <StatCard label={t('admin.orgs.clients')} value={c.clients ?? 0} />
          <StatCard label={t('admin.orgs.quotes')} value={c.quotes ?? 0} />
          <StatCard label={t('admin.orgs.invoices')} value={c.invoices ?? 0} />
          <StatCard label={t('admin.orgs.timesheets')} value={c.timesheets ?? 0} />
        </div>
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="p-2">{t('admin.orgs.eventsTitle')}</th>
                <th className="p-2">{t('admin.orgs.status')}</th>
                <th className="p-2">Date</th>
                <th className="p-2 text-right">{t('admin.orgs.photographers')}</th>
              </tr>
            </thead>
            <tbody>
              {(detail.events || []).map((e) => (
                <tr key={e.id} className="border-t">
                  <td className="p-2">{e.name}</td>
                  <td className="p-2">{e.status}</td>
                  <td className="p-2 text-xs">{e.date || '—'}</td>
                  <td className="p-2 text-right">{e.photographer_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <Input placeholder={t('admin.orgs.search')} value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()} className="max-w-sm" />
        <Button onClick={load}>{t('common.search')}</Button>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="p-2">{t('admin.orgs.owner')}</th>
              <th className="p-2">{t('admin.orgs.plan')}</th>
              <th className="p-2">{t('admin.orgs.status')}</th>
              <th className="p-2 text-right">{t('admin.orgs.members')}</th>
              <th className="p-2 text-right">{t('admin.orgs.events')}</th>
              <th className="p-2 text-right">{t('admin.orgs.photographers')}</th>
              <th className="p-2">{t('admin.orgs.lastActivity')}</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="p-4 text-center text-slate-400">{t('admin.orgs.noOrgs')}</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.org_id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => openOrg(r.org_id)}>
                <td className="p-2">
                  <div className="font-medium">{r.owner?.name || t('admin.orgs.unknownOwner')}</div>
                  <div className="text-xs text-slate-400">{r.owner?.email}</div>
                </td>
                <td className="p-2">{r.owner?.plan_id || '—'}</td>
                <td className="p-2">{r.owner?.status || '—'}</td>
                <td className="p-2 text-right">{r.member_count}</td>
                <td className="p-2 text-right">{r.event_count}</td>
                <td className="p-2 text-right">{r.photographer_count}</td>
                <td className="p-2 text-xs">{r.last_activity ? new Date(r.last_activity).toLocaleDateString() : '—'}</td>
                <td className="p-2 text-right">
                  <Button variant="ghost" size="sm">{t('admin.orgs.view')}</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export default function AdminPanel() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onSignOut = () => {
    logout();
    navigate('/backend-login');
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-950 text-slate-100 border-b border-slate-800 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-400" />
            <span className="font-semibold tracking-wide">PhotoPlan Backend</span>
            <span className="text-xs text-slate-500 font-mono ml-2">/ {user?.email}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={onSignOut} className="text-slate-300 hover:text-white">
            <LogOut className="w-4 h-4 mr-1" /> Sign out
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <h1 className="text-3xl font-bold text-slate-900 mb-6">{t('admin.title')}</h1>
        <Tabs defaultValue="overview">
          <TabsList className="mb-5 flex-wrap h-auto">
            <TabsTrigger value="overview"><Globe2 className="w-4 h-4 mr-1" /> {t('admin.tabs.overview')}</TabsTrigger>
            <TabsTrigger value="orgs"><Building2 className="w-4 h-4 mr-1" /> {t('admin.tabs.orgs')}</TabsTrigger>
            <TabsTrigger value="dashboard"><BarChart3 className="w-4 h-4 mr-1" /> {t('admin.tabs.dashboard')}</TabsTrigger>
            <TabsTrigger value="users"><UsersIcon className="w-4 h-4 mr-1" /> {t('admin.tabs.users')}</TabsTrigger>
            <TabsTrigger value="accessCodes"><KeyRound className="w-4 h-4 mr-1" /> {t('admin.tabs.accessCodes')}</TabsTrigger>
            <TabsTrigger value="subscriptions"><Layers className="w-4 h-4 mr-1" /> {t('admin.tabs.subscriptions')}</TabsTrigger>
            <TabsTrigger value="plans"><ListTree className="w-4 h-4 mr-1" /> {t('admin.tabs.plans')}</TabsTrigger>
            <TabsTrigger value="credits"><Coins className="w-4 h-4 mr-1" /> {t('admin.tabs.credits')}</TabsTrigger>
            <TabsTrigger value="settings"><SettingsIcon className="w-4 h-4 mr-1" /> {t('admin.tabs.settings')}</TabsTrigger>
          </TabsList>
          <TabsContent value="overview"><OverviewTab /></TabsContent>
          <TabsContent value="orgs"><OrgsTab /></TabsContent>
          <TabsContent value="dashboard"><DashboardTab /></TabsContent>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="accessCodes"><AccessCodesTab /></TabsContent>
          <TabsContent value="subscriptions"><SubscriptionsTab /></TabsContent>
          <TabsContent value="plans"><PlansTab /></TabsContent>
          <TabsContent value="credits"><CreditsTab /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
